import { createFileRoute, Link } from '@tanstack/react-router';
import { lazy, Suspense } from 'react';
import { PortfolioDetailAmenities } from '@/components/pages/portfolio-detail/portfolio-detail-amenities';
import { PortfolioDetailFloorPlans } from '@/components/pages/portfolio-detail/portfolio-detail-floor-plans';
import { PortfolioDetailHero } from '@/components/pages/portfolio-detail/portfolio-detail-hero';
import { PortfolioDetailSimple } from '@/components/pages/portfolio-detail/portfolio-detail-simple';
import { PortfolioDetailSpecs } from '@/components/pages/portfolio-detail/portfolio-detail-specs';
import { PortfolioDetailVision } from '@/components/pages/portfolio-detail/portfolio-detail-vision';

const PortfolioDetailGallery = lazy(() =>
  import('@/components/pages/portfolio-detail/portfolio-detail-gallery').then(
    (m) => ({ default: m.PortfolioDetailGallery }),
  ),
);
const PortfolioDetailLocation = lazy(() =>
  import('@/components/pages/portfolio-detail/portfolio-detail-location').then(
    (m) => ({ default: m.PortfolioDetailLocation }),
  ),
);

import type {
  Project,
  ProjectDetail as ProjectDetailData,
} from '@/content/projects';
import { allProjects } from '@/content/projects';
import {
  breadcrumbLd,
  generateMeta,
  organizationLd,
  productLd,
  SITE_URL,
} from '@/lib/seo';

export const Route = createFileRoute('/portfolio/$slug')({
  loader: ({ params }) => {
    const project = allProjects.find((p) => p.slug === params.slug);
    return { project: project ?? null };
  },
  component: ProjectDetail,
  pendingMs: 100,
  pendingComponent: () => (
    <main>
      <section className='relative h-230.25 overflow-hidden dark-section bg-background'>
        <div className='site-wrapper flex h-full flex-col justify-end pb-24'>
          <div className='mb-6 h-6 w-64 animate-pulse rounded bg-white/10' />
          <div className='h-20 w-3/4 animate-pulse rounded bg-white/10 md:h-28' />
        </div>
      </section>
      <section className='bg-surface-container py-20'>
        <div className='site-wrapper'>
          <div className='grid grid-cols-4 gap-12'>
            {[...Array(4)].map((_, i) => (
              <div
                key={`skeleton-${
                  // biome-ignore lint/suspicious/noArrayIndexKey: this is fine
                  i
                }`}
                className='space-y-2'
              >
                <div className='h-4 w-20 animate-pulse rounded bg-muted-foreground/20' />
                <div className='h-6 w-32 animate-pulse rounded bg-muted-foreground/20' />
              </div>
            ))}
          </div>
        </div>
      </section>
    </main>
  ),
  errorComponent: ({ error }) => (
    <main className='flex min-h-[60vh] items-center justify-center bg-surface-container'>
      <div className='text-center'>
        <h1 className='text-4xl font-serif text-foreground'>Project Error</h1>
        <p className='mt-4 text-muted-foreground'>
          {error instanceof Error
            ? error.message
            : 'Failed to load project details.'}
        </p>
        <Link
          to='/portfolio'
          className='mt-8 inline-block rounded-sm border border-border px-6 py-3 text-[10px] font-medium tracking-widest text-foreground uppercase transition-colors hover:border-brand hover:text-brand'
        >
          Back to Portfolio
        </Link>
      </div>
    </main>
  ),
  head: ({ loaderData, params }) => {
    const project = loaderData?.project ?? null;
    const meta = generateMeta({
      path: `/portfolio/${params.slug}`,
      title: project?.title ?? 'Project Details',
      description: project?.description,
      image: project?.image,
    });

    const ldMeta: Array<Record<string, unknown>> = [
      {
        'script:ld+json': organizationLd(),
      },
    ];

    if (project) {
      ldMeta.push({
        'script:ld+json': {
          '@context': 'https://schema.org',
          ...breadcrumbLd([
            { name: 'Home', url: SITE_URL },
            { name: 'Portfolio', url: `${SITE_URL}/portfolio` },
            {
              name: project.title,
              url: `${SITE_URL}/portfolio/${project.slug}`,
            },
          ]),
        },
      });

      const specs = project.detail?.specs;

      if (specs) {
        ldMeta.push({
          'script:ld+json': {
            '@context': 'https://schema.org',
            ...productLd({
              name: project.title,
              description: project.description,
              image: project.image,
              url: `${SITE_URL}/portfolio/${project.slug}`,
              status: project.status,
              location: project.location,
              area: specs.totalArea,
              units: specs.units,
            }),
          },
        });
      }
    }

    return {
      meta: [...meta.meta, ...ldMeta],
      links: [
        { rel: 'canonical', href: `${SITE_URL}/portfolio/${params.slug}` },
        ...(project?.image
          ? [
              {
                rel: 'preload' as const,
                as: 'image' as const,
                href: project.image,
              },
            ]
          : []),
      ],
    };
  },
  notFoundComponent: ProjectNotFound,
});

function ProjectNotFound() {
  return (
    <main className='flex min-h-[60vh] items-center justify-center bg-surface-container'>
      <div className='text-center'>
        <h1 className='text-6xl font-serif text-foreground'>404</h1>
        <p className='mt-4 text-muted-foreground'>Project not found</p>
        <Link
          to='/portfolio'
          className='mt-8 inline-block rounded-sm border border-border px-6 py-3 text-[10px] font-medium tracking-widest text-foreground uppercase transition-colors hover:border-brand hover:text-brand'
        >
          Back to Portfolio
        </Link>
      </div>
    </main>
  );
}

function ProjectDetail() {
  const { project } = Route.useLoaderData();

  if (!project) return <ProjectNotFound />;

  return !project.detail ? (
    <PortfolioDetailSimple project={project} />
  ) : (
    <FullProjectView project={project} detail={project.detail} />
  );
}

function FullProjectView({
  project,
  detail,
}: {
  project: Project;
  detail: ProjectDetailData;
}) {
  return (
    <main>
      <PortfolioDetailHero project={project} detail={detail} />
      <PortfolioDetailSpecs specs={detail.specs} />
      <PortfolioDetailFloorPlans floorPlans={detail.floorPlans} />
      <PortfolioDetailVision vision={detail.vision} />
      <Suspense
        fallback={
          <section className='bg-white py-24'>
            <div className='site-wrapper'>
              <div className='h-200 w-full animate-pulse rounded bg-muted-foreground/10' />
            </div>
          </section>
        }
      >
        <PortfolioDetailGallery
          images={detail.gallery}
          projectTitle={project.title}
        />
      </Suspense>
      <PortfolioDetailAmenities amenities={detail.amenities} />
      <Suspense
        fallback={
          <section className='bg-white py-32'>
            <div className='site-wrapper'>
              <div className='h-150 w-full animate-pulse rounded bg-muted-foreground/10' />
            </div>
          </section>
        }
      >
        <PortfolioDetailLocation location={detail.location} />
      </Suspense>
    </main>
  );
}
